import { useState, useEffect, useCallback } from 'react'
import api from '../lib/api'

export interface VentaOffline {
  id: string
  items: Array<{ productoId: string; nombre: string; cantidad: number; precio: number }>
  total: number
  clienteId?: string
  sede: string
  metodoPago: string
  timestamp: number
  synced?: boolean
  error?: string
}

const QUEUE_KEY = 'aqua_offline_ventas_queue'

function loadQueue(): VentaOffline[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]')
  } catch {
    return []
  }
}

function saveQueue(queue: VentaOffline[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
}

export function useOfflineQueue() {
  const [queue, setQueue] = useState<VentaOffline[]>(loadQueue)
  const [syncing, setSyncing] = useState(false)

  const pendingCount = queue.filter(v => !v.synced).length

  // Sincronizar en memoria y en localStorage
  const refresh = useCallback(() => {
    setQueue(loadQueue())
  }, [])

  // Agregar venta a la cola
  const enqueue = useCallback((venta: Omit<VentaOffline, 'id' | 'timestamp'>) => {
    const item: VentaOffline = {
      ...venta,
      id: `offline_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      timestamp: Date.now(),
    }
    const current = loadQueue()
    const next = [...current, item]
    saveQueue(next)
    setQueue(next)
    return item
  }, [])

  // Intentar sincronizar toda la cola pendiente
  const syncQueue = useCallback(async (token: string) => {
    const current = loadQueue()
    const pending = current.filter(v => !v.synced)
    if (pending.length === 0) return

    setSyncing(true)
    const updated = [...current]

    for (const venta of pending) {
      try {
        // Tiene que ser la MISMA ruta y el MISMO formato que usa la caja
        // cuando hay señal. Antes esto apuntaba a "/api/ventas", que no existe
        // —el backend es /orders— y encima con axios pelado, que en producción
        // pegaba contra el dominio del frontend y no contra el servidor. El
        // resultado: toda venta hecha sin señal se quedaba atrapada en el
        // dispositivo reintentando contra una dirección inexistente, y el
        // colaborador la daba por registrada.
        await api.post('/orders', {
          items: venta.items.map(i => ({
            productoId: Number(i.productoId),
            cantidad: i.cantidad,
            precio: i.precio,
          })),
          clienteId: venta.clienteId ? Number(venta.clienteId) : undefined,
          metodoPago: venta.metodoPago,
          sede: venta.sede,
          enviarWhatsapp: false,
        }, { headers: { Authorization: `Bearer ${token}` } })
        const idx = updated.findIndex(v => v.id === venta.id)
        if (idx !== -1) updated[idx] = { ...updated[idx], synced: true, error: undefined }
      } catch (err: any) {
        const idx = updated.findIndex(v => v.id === venta.id)
        if (idx !== -1)
          updated[idx] = {
            ...updated[idx],
            error: err?.response?.data?.message ?? 'Error de red',
          }
      }
    }

    saveQueue(updated)
    setQueue(updated)
    setSyncing(false)
  }, [])

  // Limpiar ventas ya sincronizadas
  const clearSynced = useCallback(() => {
    const current = loadQueue()
    const next = current.filter(v => !v.synced)
    saveQueue(next)
    setQueue(next)
  }, [])

  // Eliminar una entrada de la cola
  const removeFromQueue = useCallback((id: string) => {
    const current = loadQueue()
    const next = current.filter(v => v.id !== id)
    saveQueue(next)
    setQueue(next)
  }, [])

  // Recargar cuando la ventana recupera foco
  useEffect(() => {
    window.addEventListener('focus', refresh)
    return () => window.removeEventListener('focus', refresh)
  }, [refresh])

  return { queue, pendingCount, syncing, enqueue, syncQueue, clearSynced, removeFromQueue }
}
